// src/pages/LiveMap.js - Fixed Rating Error
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker, InfoWindow, Circle } from '@react-google-maps/api';

// Replace with your Google Maps API Key
const GOOGLE_MAPS_API_KEY = 'AIzaSyBGVjryVIkIjQmezCTG-_k4O0Jci1gZoOs';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 0.3136,
  lng: 32.5811,
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

function LiveMap() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(13);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchDrivers();
    const interval = setInterval(() => {
      if (autoRefresh) fetchDrivers();
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchDrivers = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/all-drivers`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      const allDrivers = response.data.drivers || [];
      console.log(`✅ Fetched ${allDrivers.length} drivers`);
      
      // Process drivers with valid coordinates and safe values
      const processedDrivers = allDrivers.map(d => ({
        ...d,
        lat: d.current_lat ? parseFloat(d.current_lat) : null,
        lng: d.current_lng ? parseFloat(d.current_lng) : null,
        hasLocation: d.current_lat !== null && d.current_lng !== null,
        isOnline: d.is_online || false,
        vehicle_type: d.vehicle_type || 'boda',
        // ✅ FIX: Safely parse rating
        rating: d.average_rating ? parseFloat(d.average_rating) : 0,
        total_rides: d.total_rides ? parseInt(d.total_rides) : 0,
      }));
      
      // Filter drivers with valid locations for the map
      const driversWithLocation = processedDrivers.filter(d => d.hasLocation);
      
      // If we have drivers with location, center map on them
      if (driversWithLocation.length > 0 && !mapLoaded) {
        const avgLat = driversWithLocation.reduce((sum, d) => sum + d.lat, 0) / driversWithLocation.length;
        const avgLng = driversWithLocation.reduce((sum, d) => sum + d.lng, 0) / driversWithLocation.length;
        setMapCenter({ lat: avgLat, lng: avgLng });
        setZoom(12);
      }
      
      setDrivers(processedDrivers);
      setLastUpdated(new Date().toLocaleTimeString());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError(error.response?.data?.error || 'Failed to load drivers');
      setLoading(false);
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);
    console.log('✅ Map loaded successfully');
  }, []);

  const onMapError = useCallback(() => {
    console.error('❌ Map failed to load');
    setError('Failed to load Google Maps. Please check your API key.');
    setLoading(false);
  }, []);

  const onlineCount = drivers.filter(d => d.isOnline).length;
  const offlineCount = drivers.filter(d => !d.isOnline).length;
  const driversWithLocation = drivers.filter(d => d.hasLocation).length;

  // Get marker color based on vehicle type and status
  const getMarkerColor = (driver) => {
    if (!driver.isOnline) return '#94a3b8';
    const colors = {
      boda: '#10b981',
      boda_electric: '#34d399',
      car: '#3b82f6',
      car_xl: '#8b5cf6',
    };
    return colors[driver.vehicle_type] || colors.boda;
  };

  // Vehicle emojis
  const getVehicleEmoji = (type) => {
    const emojis = {
      boda: '🛵',
      boda_electric: '⚡',
      car: '🚗',
      car_xl: '🚙',
    };
    return emojis[type] || '🛵';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f1f5f9' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading drivers...</div>
          <div style={{ fontSize: '14px', color: '#94a3b8', marginTop: '8px' }}>Fetching real-time location data</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', height: '100%', minHeight: 'calc(100vh - 80px)', backgroundColor: '#f1f5f9' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>Live Driver Tracking</h1>
          <div style={{ display: 'flex', gap: '20px', marginTop: '6px', fontSize: '15px', flexWrap: 'wrap' }}>
            <span style={{ color: '#10b981', fontWeight: '500' }}>🟢 {onlineCount} Online</span>
            <span style={{ color: '#94a3b8', fontWeight: '500' }}>🔴 {offlineCount} Offline</span>
            <span style={{ color: '#f97316', fontWeight: '500' }}>📊 {drivers.length} Total</span>
            {driversWithLocation > 0 && (
              <span style={{ color: '#3b82f6', fontWeight: '500' }}>📍 {driversWithLocation} with location</span>
            )}
            {lastUpdated && (
              <span style={{ color: '#64748b', fontWeight: '400', fontSize: '13px' }}>
                🕐 Updated: {lastUpdated}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            style={{
              padding: '10px 20px',
              backgroundColor: autoRefresh ? '#10b981' : '#64748b',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {autoRefresh ? '🔄 Auto-refresh ON' : '⏸️ Auto-refresh OFF'}
          </button>
          <button
            onClick={fetchDrivers}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            🔄 Refresh Now
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Map Container */}
      <div style={{
        backgroundColor: '#f0f2f5',
        borderRadius: '16px',
        height: '550px',
        position: 'relative',
        overflow: 'hidden',
        marginBottom: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid #e8ecf1',
      }}>
        <LoadScript
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          onError={onMapError}
          onLoad={() => console.log('✅ Google Maps script loaded')}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={zoom}
            options={mapOptions}
            onLoad={onMapLoad}
            onZoomChanged={() => {
              if (mapRef.current) {
                setZoom(mapRef.current.getZoom());
              }
            }}
            onDragEnd={() => {
              if (mapRef.current) {
                const center = mapRef.current.getCenter();
                setMapCenter({
                  lat: center.lat(),
                  lng: center.lng(),
                });
              }
            }}
          >
            {/* Driver Markers */}
            {drivers.filter(d => d.hasLocation).map((driver) => {
              const position = { lat: driver.lat, lng: driver.lng };
              const isOnline = driver.isOnline;
              const vehicleType = driver.vehicle_type || 'boda';
              const emoji = getVehicleEmoji(vehicleType);
              const markerColor = getMarkerColor(driver);
              
              return (
                <Marker
                  key={driver.id}
                  position={position}
                  onClick={() => setSelectedDriver(driver)}
                  icon={{
                    path: 'M0,0 C-12,-12 -12,-24 0,-28 C12,-24 12,-12 0,0',
                    fillColor: markerColor,
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2,
                    scale: 1.2,
                  }}
                  label={{
                    text: emoji,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {selectedDriver?.id === driver.id && (
                    <InfoWindow
                      position={position}
                      onCloseClick={() => setSelectedDriver(null)}
                    >
                      <div style={{ padding: '8px', maxWidth: '250px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {driver.full_name || `Driver ${driver.id}`}
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                          {driver.phone_number}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            backgroundColor: isOnline ? '#dcfce7' : '#f1f5f9',
                            color: isOnline ? '#166534' : '#64748b',
                          }}>
                            {isOnline ? '🟢 Online' : '🔴 Offline'}
                          </span>
                          <span style={{
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                          }}>
                            {vehicleType}
                          </span>
                          {/* ✅ FIX: Safe rating display */}
                          {driver.rating > 0 && (
                            <span style={{
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                            }}>
                              ⭐ {driver.rating.toFixed(1)}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                          🚗 {driver.total_rides || 0} rides
                        </div>
                        <button
                          onClick={() => window.location.href = `/drivers/${driver.id}`}
                          style={{
                            marginTop: '10px',
                            padding: '6px 16px',
                            backgroundColor: '#f97316',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            width: '100%',
                          }}
                        >
                          View Profile
                        </button>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              );
            })}

            {/* Online status circles */}
            {drivers.filter(d => d.hasLocation && d.isOnline).map((driver) => (
              <Circle
                key={`circle-${driver.id}`}
                center={{ lat: driver.lat, lng: driver.lng }}
                radius={100}
                options={{
                  fillColor: '#10b981',
                  fillOpacity: 0.1,
                  strokeColor: '#10b981',
                  strokeOpacity: 0.3,
                  strokeWeight: 1,
                }}
              />
            ))}
          </GoogleMap>
        </LoadScript>

        {/* Map Loading Overlay */}
        {!mapLoaded && !error && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f0f2f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🗺️</div>
              <div style={{ fontSize: '16px', color: '#64748b' }}>Loading map...</div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>Please wait</div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '12px 18px',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          fontSize: '13px',
          zIndex: 10,
          border: '1px solid rgba(255,255,255,0.6)',
        }}>
          <div style={{ fontWeight: '600', marginBottom: '6px', color: '#1e293b' }}>📍 Legend</div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#10b981' }} />
              Online ({onlineCount})
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#94a3b8' }} />
              Offline ({offlineCount})
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>🛵 Boda</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>🚗 Car</span>
          </div>
        </div>

        {/* Info */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'rgba(255,255,255,0.9)',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '12px',
          color: '#94a3b8',
          zIndex: 10,
          border: '1px solid rgba(255,255,255,0.6)',
        }}>
          {driversWithLocation} drivers shown • {driversWithLocation === 0 && 'No location data'}
        </div>
      </div>

      {/* Driver List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        maxHeight: '220px',
        overflow: 'auto',
        border: '1px solid #e8ecf1',
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', 
          gap: '8px',
        }}>
          {drivers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', gridColumn: '1 / -1' }}>
              No drivers found
            </div>
          ) : (
            drivers.map((driver) => (
              <div
                key={driver.id}
                onClick={() => {
                  setSelectedDriver(driver);
                  if (driver.hasLocation) {
                    setMapCenter({ lat: driver.lat, lng: driver.lng });
                    setZoom(15);
                  }
                }}
                style={{
                  padding: '10px 14px',
                  backgroundColor: selectedDriver?.id === driver.id ? '#fef3c7' : '#f8fafc',
                  borderRadius: '8px',
                  border: selectedDriver?.id === driver.id ? '2px solid #f97316' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {driver.full_name || `Driver ${driver.id}`}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {driver.vehicle_type || 'Unknown'}
                    {driver.hasLocation ? ' 📍' : ' ❌ No location'}
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  flexShrink: 0,
                }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: driver.isOnline ? '#10b981' : '#94a3b8',
                    boxShadow: driver.isOnline ? '0 0 8px rgba(16,185,129,0.4)' : 'none',
                  }} />
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {/* ✅ FIX: Safe rating display in list */}
                    {driver.rating > 0 ? `⭐${driver.rating.toFixed(1)}` : ''}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default LiveMap;